import { Directive, Input, ElementRef, OnInit, OnChanges, OnDestroy, SimpleChanges, Renderer2 } from '@angular/core';
import { PermissionsService } from '../../auth/services/permissions.service';
import { NotificationService } from '@app/core/services/notification.service';

/**
 * Diretiva de permissões do PWA Angular — equivalente ao `bf-permissions` do AngularJS.
 * Aceita state names (`main.servidor...`) ou pageIds diretos (`EducacaoEntidadePageMapping`).
 * Sem permissão: desabilita o elemento (disabled + tooltip) por padrão, ou esconde com
 * `appPermissionsHide`.
 */
@Directive({
  selector: '[appPermissions]'
})
export class AppPermissionsDirective implements OnInit, OnChanges, OnDestroy {

  /**
   * State name ou pageId para verificar permissão.
   * Ex: 'main.servidor.configurando.parametros.entidade' ou 'EducacaoEntidadePageMapping'.
   * String vazia = usar operation com o state atual (como bf-permissions sem valor).
   */
  @Input('appPermissions') stateOrPageId = '';

  /** Operação a verificar (ex: 'criar', 'editar', 'excluir', 'alterar'). */
  @Input() appPermissionsOperation = '';

  /** Se presente, exige que o usuário seja admin. */
  @Input() appPermissionsAdminOnly: boolean | '' = false;

  /** Equivalente ao `ui-sref` do AngularJS para checar estado alvo. */
  @Input() appPermissionsUiSref = '';

  /** Equivalente ao `page-id` do AngularJS para checar page mapping direto. */
  @Input() appPermissionsPageId = '';

  /** Permite regra OR entre múltiplos states/pageIds (equiv. vm.permissionsAbonoJustificativa). */
  @Input() appPermissionsAny: string[] | string = [];

  /** Se presente, esconde o elemento em vez de desabilitar. */
  @Input() appPermissionsHide: boolean | '' = false;

  private static readonly DISABLED_TOOLTIP = 'Você não tem permissão para acessar este recurso.';
  private blockedInteractionHandler?: (event: Event) => void;
  private disabledBgColor?: string;
  private lastFeedbackAt = 0;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private permissionsService: PermissionsService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.applyPermission();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.applyPermission();
  }

  ngOnDestroy(): void {
    this.unbindBlockedInteractions();
  }

  private applyPermission(): void {
    const hasPermission = this.checkPermission();

    if (hasPermission) {
      this.enableElement();
    } else {
      this.disableElement();
    }
  }

  private checkPermission(): boolean {
    if (this.appPermissionsAdminOnly === '' || this.appPermissionsAdminOnly === true) {
      return this.permissionsService.isAdmin();
    }

    if (this.appPermissionsOperation) {
      const pageId = this.resolveCurrentPageIdForOperation();
      if (pageId) {
        return !this.permissionsService.isRevokedOperation(pageId, this.appPermissionsOperation);
      }
      return false;
    }

    if (this.stateOrPageId) {
      return this.permissionsService.checkPermissionForStateName(this.stateOrPageId);
    }

    // Regra OR: qualquer permissão da lista libera o elemento.
    const anyList = this.normalizeAnyList(this.appPermissionsAny);
    if (anyList.length > 0) {
      return anyList.some(item => this.permissionsService.checkPermissionForStateName(item));
    }

    if (this.appPermissionsUiSref) {
      return this.permissionsService.checkPermissionForStateName(this.appPermissionsUiSref);
    }

    if (this.appPermissionsPageId) {
      return this.permissionsService.checkPermissionForPageId(this.appPermissionsPageId);
    }

    // Paridade com AngularJS: sem configuração explícita, negar.
    return false;
  }

  private normalizeAnyList(value: string[] | string): string[] {
    if (Array.isArray(value)) {
      return value.filter(Boolean).map(v => String(v).trim()).filter(Boolean);
    }
    if (typeof value === 'string') {
      return value.split(',').map(v => v.trim()).filter(Boolean);
    }
    return [];
  }

  private resolveCurrentPageIdForOperation(): string | null {
    if (this.appPermissionsPageId) {
      return this.appPermissionsPageId;
    }
    if (this.appPermissionsUiSref) {
      return this.permissionsService.resolvePageId(this.appPermissionsUiSref);
    }
    if (this.stateOrPageId) {
      return this.permissionsService.resolvePageId(this.stateOrPageId);
    }
    return this.permissionsService.getCurrentPageId();
  }

  private enableElement(): void {
    const el = this.el.nativeElement;
    const defaultTitle = el.getAttribute('default-title');

    this.unbindBlockedInteractions();
    this.renderer.removeAttribute(el, 'disabled');
    this.renderer.removeAttribute(el, 'aria-disabled');
    this.renderer.removeClass(el, 'permissions-disabled');
    this.renderer.removeStyle(el, 'display');
    if (defaultTitle) {
      this.renderer.setAttribute(el, 'title', defaultTitle);
    } else {
      this.renderer.removeAttribute(el, 'title');
    }
    this.renderer.setStyle(el, 'pointer-events', '');
    this.renderer.setStyle(el, 'opacity', '');
    this.renderer.removeStyle(el, 'transform');
    this.renderer.removeStyle(el, 'transition');
    this.renderer.removeStyle(el, 'background-color');
    this.disabledBgColor = undefined;
  }

  private disableElement(): void {
    const el = this.el.nativeElement;
    const shouldHide = this.appPermissionsHide === true ||
      el.classList.contains('permissions-hide');
    const isTouchDevice = this.isTouchDevice();

    if (shouldHide) {
      this.unbindBlockedInteractions();
      this.renderer.addClass(el, 'permissions-disabled');
      this.renderer.setStyle(el, 'display', 'none');
    } else {
      // Garante visibilidade quando o modo é "desabilitar" (não esconder),
      // mesmo que o elemento tenha sido ocultado anteriormente.
      this.renderer.removeStyle(el, 'display');
      // Só captura o title original na PRIMEIRA vez que desabilita — se já existe
      // default-title salvo, o elemento já está bloqueado agora (ex: ngOnChanges disparando
      // de novo enquanto ainda sem permissão) e o title atual já é o tooltip de bloqueio,
      // não o original. Sobrescrever aqui perderia o valor original de verdade.
      const currentTitle = el.getAttribute('title');
      const jaTemDefaultTitle = el.getAttribute('default-title') !== null;
      if (currentTitle !== null && !jaTemDefaultTitle) {
        this.renderer.setAttribute(el, 'default-title', currentTitle);
      }
      // Não usar disabled nativo: ele bloqueia eventos e impede feedback no mobile.
      this.renderer.removeAttribute(el, 'disabled');
      this.renderer.setAttribute(el, 'aria-disabled', 'true');
      this.disabledBgColor = getComputedStyle(el).backgroundColor;
      if (this.disabledBgColor) {
        this.renderer.setStyle(el, 'background-color', this.disabledBgColor);
      }
      // Evita "active:scale" e transições visuais no toque quando bloqueado.
      this.renderer.setStyle(el, 'transform', 'none');
      this.renderer.setStyle(el, 'transition', 'none');
      this.bindBlockedInteractions();
      this.renderer.addClass(el, 'permissions-disabled');
      this.renderer.setAttribute(el, 'title', AppPermissionsDirective.DISABLED_TOOLTIP);
      this.renderer.removeStyle(el, 'pointer-events');
      this.renderer.setStyle(el, 'opacity', '0.5');
      this.renderer.setStyle(el, 'cursor', 'not-allowed');
    }
  }

  private isTouchDevice(): boolean {
    return window.matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;
  }

  private bindBlockedInteractions(): void {
    if (this.blockedInteractionHandler) {
      return;
    }
    const el = this.el.nativeElement;
    this.blockedInteractionHandler = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      this.notificationService.warning({
        title: 'Sem permissão',
        message: AppPermissionsDirective.DISABLED_TOOLTIP,
        duration: 3000
      });
      // Fallback para browsers mobile onde toast/title não aparecem de forma consistente.
      const now = Date.now();
      if (now - this.lastFeedbackAt > 1200) {
        this.lastFeedbackAt = now;
        window.alert(AppPermissionsDirective.DISABLED_TOOLTIP);
      }
    };
    el.addEventListener('click', this.blockedInteractionHandler, true);
    el.addEventListener('touchstart', this.blockedInteractionHandler, { capture: true, passive: false });
    el.addEventListener('pointerdown', this.blockedInteractionHandler, true);
  }

  private unbindBlockedInteractions(): void {
    if (!this.blockedInteractionHandler) {
      return;
    }
    const el = this.el.nativeElement;
    el.removeEventListener('click', this.blockedInteractionHandler, true);
    el.removeEventListener('touchstart', this.blockedInteractionHandler, true);
    el.removeEventListener('pointerdown', this.blockedInteractionHandler, true);
    this.blockedInteractionHandler = undefined;
  }
}
